import { useMemo } from 'react';
import {
  CustomizationDataDto,
  TableColumnConfigDto,
  TableConfigDto,
} from '@asset-metrix/portal-gateway-client-js';
import { useQuery } from '@tanstack/react-query';

import { fetchTableCustomization } from '../../api/portalGateway/tableCustomization';

import { applyCustomizationToColumnConfig } from './applyCustomizationToColumnConfigs';

export type CustomColumnWidths = {
  [id: TableColumnConfigDto['id']]: { width: number };
};

export type UseCustomizationResult = {
  isCustomizable: boolean;
  isCustomized: boolean;
  customizedColumns: TableColumnConfigDto[];
  customizationData?: CustomizationDataDto;
  isColumnInitiallyHidden: boolean;
};

export function useCustomization(
  widgetId: string,
  tableConfig: TableConfigDto,
  customColumnWidths: CustomColumnWidths = {}
): UseCustomizationResult {
  const { data: customizationData } = useQuery({
    queryKey: ['customization', { widgetId }],
    queryFn: fetchTableCustomization,
    enabled: tableConfig.customizable,
  });

  const customizedColumns = useMemo(() => {
    return tableConfig.customizable
      ? applyCustomizationToColumnConfig(
          tableConfig.columns,
          customizationData?.data.columns,
          customColumnWidths
        )
      : tableConfig.columns;
  }, [
    customizationData?.data.columns,
    tableConfig.columns,
    tableConfig.customizable,
    customColumnWidths,
  ]);

  const isCustomized = useMemo(() => {
    return (
      customizationData?.data.columns != null &&
      customizationData?.data.columns.length > 0
    );
  }, [customizationData?.data.columns]);

  let isColumnInitiallyHidden = false;
  for (const col of tableConfig.columns) {
    if (col.initiallyUnvisibleToUser && !col.hidden) {
      isColumnInitiallyHidden = true;
    }
  }

  if (!tableConfig.customizable) {
    return {
      isCustomizable: false,
      isCustomized: false,
      customizedColumns: tableConfig.columns,
      isColumnInitiallyHidden,
    };
  }

  return {
    isCustomizable: true,
    isCustomized,
    customizedColumns,
    customizationData: customizationData?.data,
    isColumnInitiallyHidden,
  };
}
